import { format } from 'date-fns';

import { DATE_FORMAT } from '@/lib/constants';
import { colors } from '@/theme/constants';
import { AxesSettings, BaseSettings, GridSettings, PieSettings, RadialSettings, SeriesSettings } from '@/types/charts';
import { tryParseDate } from '@/utils/format/date-time';

const BASE_SETTINGS: BaseSettings = {
  chart: {
    height: 200,
    margin: { top: 0, right: 5, bottom: 0, left: 5 },
  },
  tooltip: {
    valueSuffix: '',
    tooltipSubHeading: '',
    hideTooltipLabel: false,
    cursor: {
      stroke: colors.grey[400],
      strokeWidth: 1,
      strokeDasharray: '4, 4',
    },
    hide: false,
  },
  legend: {
    hide: true,
  },
  labelList: {
    position: 'top',
    offset: 8,
    className: 'fill-white',
    fontSize: 12,
    hide: true,
  },
};

export const DEFAULT_AXES_SETTINGS: AxesSettings = {
  axes: {
    xAxis: {
      axisLine: false,
      tickMargin: 8,
      stroke: colors.charts.xAxis,
      hide: false,
      dataKey: 'date',
      tickFormatter: (value: string) => {
        const date = tryParseDate(value);
        return date ? format(date, DATE_FORMAT) : '';
      },
    },
    yAxis: {
      tickLine: false,
      axisLine: false,
      tickMargin: 8,
      hide: true,
      dataKey: undefined,
    },
  },
};

const DEFAULT_SERIES_SETTINGS: SeriesSettings = {
  series: {
    dataKey: '',
    stroke: '',
    strokeWidth: 2,
    dot: false,
    activeDot: {
      r: 4,
      stroke: 'white',
      strokeWidth: 2,
      fill: '',
    },
    type: 'monotone',
  },
};

const DEFAULT_GRID_SETTINGS: GridSettings = {
  grid: {
    vertical: false,
    horizontal: true,
    strokeDasharray: '3, 2',
    stroke: colors.charts.xAxis,
    strokeWidth: 1,
  },
};

const DEFAULT_PIE_SETTINGS: PieSettings = {
  pie: {
    dataKey: '',
    nameKey: '',
    label: false,
    innerRadius: 0,
    startAngle: 90,
    endAngle: 450,
    isAnimationActive: true,
  },
  innerStats: {
    stat: undefined,
    label: undefined,
  },
};

export const DEFAULT_RADIAL_SETTINGS: RadialSettings = {
  polarGrid: {
    radialLines: false,
    stroke: 'none',
    className: '',
    gridType: 'circle',
    polarRadius: [86, 74],
    fill: 'white',
  },
  radialBar: {
    background: false,
    cornerRadius: 10,
    dataKey: '',
  },
  polarRadiusAxis: {
    tick: false,
    tickLine: false,
    axisLine: false,
  },
};

export const CHART_CONFIG = {
  line: {
    ...BASE_SETTINGS,
    ...DEFAULT_AXES_SETTINGS,
    ...DEFAULT_GRID_SETTINGS,
    ...DEFAULT_SERIES_SETTINGS,
  },
  area: {
    ...BASE_SETTINGS,
    ...DEFAULT_AXES_SETTINGS,
    ...DEFAULT_GRID_SETTINGS,
    ...DEFAULT_SERIES_SETTINGS,
  },
  bar: {
    ...BASE_SETTINGS,
    ...DEFAULT_AXES_SETTINGS,
    ...DEFAULT_GRID_SETTINGS,
    ...DEFAULT_SERIES_SETTINGS,
    chart: {
      ...BASE_SETTINGS.chart,
      layout: 'horizontal',
    },
    series: {
      radius: 4,
    },
  },
  pie: {
    ...BASE_SETTINGS,
    tooltip: {
      ...BASE_SETTINGS.tooltip,
      hideTooltipLabel: true,
    },
    ...DEFAULT_PIE_SETTINGS,
  },
  radial: {
    ...BASE_SETTINGS,
    chart: {
      ...BASE_SETTINGS.chart,
      startAngle: 0,
      endAngle: 360,
      innerRadius: 80,
      outerRadius: 110,
    },
    ...DEFAULT_RADIAL_SETTINGS,
    tooltip: {
      ...BASE_SETTINGS.tooltip,
      hide: true,
    },
  },
};
